import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { ChangeEvent, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DebounceField } from '../../DebounceField/DebounceField.tsx';
import {
  getSearchFieldInputStyles,
  SEARCH_FIELD_CLEAR_BUTTON_STYLES,
  SEARCH_FIELD_CONTAINER_STYLES,
  SEARCH_FIELD_INPUT_WRAPPER_STYLES,
} from '../constants.ts';

export type SearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  prefixElement?: ReactNode;
  dataTest?: string;
  debounceMs?: number;
};

export const SearchField = ({
  value,
  onChange,
  placeholder,
  prefixElement,
  dataTest = 'search-field',
  debounceMs,
}: SearchFieldProps) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const hasPrefixElement = Boolean(prefixElement);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );
  const handleClear = useCallback(() => onChange(''), [onChange]);

  return (
    <Box sx={SEARCH_FIELD_CONTAINER_STYLES} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {prefixElement}
      <Box sx={SEARCH_FIELD_INPUT_WRAPPER_STYLES}>
        <DebounceField
          fullWidth
          debounceTimeout={debounceMs}
          placeholder={placeholder ?? t('Search')}
          slotProps={{ htmlInput: { 'aria-label': placeholder ?? t('Search') } }}
          value={value}
          onChange={handleChange}
          size="small"
          sx={getSearchFieldInputStyles(hasPrefixElement, isHovered, Boolean(value))}
          data-test={dataTest}
        />
        {value ? (
          <IconButton
            aria-label={t('Clear search')}
            onClick={handleClear}
            size="small"
            data-test={`${dataTest}-clear-button`}
            sx={SEARCH_FIELD_CLEAR_BUTTON_STYLES}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
};
